import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, FileDown, Loader2, XCircle } from "lucide-react";
import { api, API } from "../lib/api";

const MAX_POLLS = 8;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [inquiryId, setInquiryId] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setInquiryId(data.inquiry_id);
          setState("paid");
          return;
        }
        if (data.payment_status === "expired" || data.status === "failed") {
          setState("failed");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
      } else {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const downloadUrl = inquiryId ? `${API}/public/reports/${inquiryId}/report.pdf?session_id=${sessionId}` : null;

  return (
    <div data-testid="payment-success-page" className="py-32 md:py-44 bg-white">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={36} className="animate-spin text-[#C1A74A] mx-auto mb-8" />
            <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#113835]">Confirming your payment…</h1>
            <p className="mt-4 text-gray-500 font-light">Please don't close this window.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="w-20 h-20 bg-[#113835] text-[#C1A74A] flex items-center justify-center mx-auto mb-10">
              <CheckCircle2 size={32} strokeWidth={1.25} />
            </div>
            <p className="tt-eyebrow mb-4 justify-center flex">Payment Successful</p>
            <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#113835] tracking-tight">
              Your verified report is ready.
            </h1>
            <p className="mt-5 text-gray-600 font-light">Thank you. Download your expert-verified Terraa Score report below.</p>
            <a
              href={downloadUrl}
              data-testid="payment-download-report"
              className="tt-btn-gold mt-10 inline-flex"
              download
            >
              <FileDown size={16} strokeWidth={1.5} /> Download Verified PDF Report
            </a>
            <p className="mt-6 text-xs text-gray-400 font-light">Keep this page's link safe — you can return to re-download your report.</p>
          </div>
        )}

        {(state === "failed" || state === "error") && (
          <div data-testid="payment-failed">
            <XCircle size={40} className="text-red-600 mx-auto mb-8" strokeWidth={1.25} />
            <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#113835]">Payment was not completed.</h1>
            <p className="mt-4 text-gray-500 font-light">No charge was made. You can try again from the rating page.</p>
            <Link to="/" className="tt-btn-outline mt-10 inline-flex" data-testid="payment-home-link">Back to Home</Link>
          </div>
        )}

        {state === "timeout" && (
          <div data-testid="payment-timeout">
            <Loader2 size={32} className="text-[#C1A74A] mx-auto mb-8" />
            <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#113835]">Still confirming your payment…</h1>
            <p className="mt-4 text-gray-500 font-light">
              This is taking longer than usual. Refresh this page in a minute — your download will appear once payment is confirmed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
